import { useState, type FormEvent, type ChangeEvent } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Select from '../../../components/ui/Select';
import * as LucideIcons from "lucide-react";

interface FormData {
    fullName: string;
    position: string;
    assembly: string;
    assemblySize: string;
    email: string;
    phone: string;
    whatsappPreferred: boolean;
    currentSystem: string;
    urgency: string;
    challenges: string;
}

interface FormErrors {
    fullName?: string;
    position?: string;
    assembly?: string;
    assemblySize?: string;
    email?: string;
    phone?: string;
    currentSystem?: string;
    urgency?: string;
}

interface SelectOption {
    value: string;
    label: string;
}

interface Benefit {
    icon: string;
    title: string;
    description: string;
}

const DemoRequestSection = () => {
    const [formData, setFormData] = useState<FormData>({
        fullName: '',
        position: '',
        assembly: '',
        assemblySize: '',
        email: '',
        phone: '',
        whatsappPreferred: false,
        currentSystem: '',
        urgency: '',
        challenges: ''
    });
    const [errors, setErrors] = useState<FormErrors>({});
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [isSubmitted, setIsSubmitted] = useState<boolean>(false);
    const [slotsRemaining, setSlotsRemaining] = useState<number>(7);

    const assemblyOptions: SelectOption[] = [
        { value: 'kumasi-metro', label: 'Kumasi Metropolitan Assembly' },
        { value: 'accra-metro', label: 'Accra Metropolitan Assembly' },
        { value: 'tamale-metro', label: 'Tamale Metropolitan Assembly' },
        { value: 'cape-coast-metro', label: 'Cape Coast Metropolitan Assembly' },
        { value: 'sekondi-metro', label: 'Sekondi-Takoradi Metropolitan Assembly' },
        { value: 'tema-metro', label: 'Tema Metropolitan Assembly' },
        { value: 'other', label: 'Other Assembly' }
    ];

    const assemblySizeOptions: SelectOption[] = [
        { value: 'metro', label: 'Metropolitan (>250,000 population)' },
        { value: 'municipal', label: 'Municipal (95,000-250,000 population)' },
        { value: 'district', label: 'District (<95,000 population)' }
    ];

    const positionOptions: SelectOption[] = [
        { value: 'chief-executive', label: 'Chief Executive' },
        { value: 'finance-director', label: 'Finance Director' },
        { value: 'revenue-manager', label: 'Revenue Manager' },
        { value: 'it-manager', label: 'IT Manager' },
        { value: 'deputy-ce', label: 'Deputy Chief Executive' },
        { value: 'other', label: 'Other Position' }
    ];

    const currentSystemOptions: SelectOption[] = [
        { value: 'manual-only', label: 'Manual/Paper-based only' },
        { value: 'basic-software', label: 'Basic accounting software' },
        { value: 'custom-system', label: 'Custom-built system' },
        { value: 'mixed', label: 'Mixed manual and digital' },
        { value: 'none', label: 'No formal system' }
    ];

    const urgencyOptions: SelectOption[] = [
        { value: 'immediate', label: 'Immediate (Within 1 month)' },
        { value: 'quarter', label: 'This Quarter (1-3 months)' },
        { value: 'half-year', label: 'Within 6 months' },
        { value: 'planning', label: 'Planning for next year' }
    ];

    const handleInputChange = (field: keyof FormData, value: string | boolean) => {
        setFormData(prev => ({
            ...prev,
            [field]: value
        }));

        // Clear error when user starts typing
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const handleSelectChange = (field: keyof FormData, value: string | number | (string | number)[] | undefined) => {
        setFormData(prev => ({
            ...prev,
            [field]: value || ''
        }));

        // Clear error when user selects
        if (errors[field as keyof FormErrors]) {
            setErrors(prev => ({
                ...prev,
                [field]: ''
            }));
        }
    };

    const validateForm = (): boolean => {
        const newErrors: FormErrors = {};

        if (!formData.fullName.trim()) {
            newErrors.fullName = 'Full name is required';
        }

        if (!formData.position) {
            newErrors.position = 'Position is required';
        }

        if (!formData.assembly) {
            newErrors.assembly = 'Assembly selection is required';
        }

        if (!formData.assemblySize) {
            newErrors.assemblySize = 'Assembly size is required';
        }

        if (!formData.email.trim()) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Please enter a valid email address';
        }

        if (!formData.phone.trim()) {
            newErrors.phone = 'Phone number is required';
        } else if (!/^(\+233|0)[0-9]{9}$/.test(formData.phone.replace(/\s/g, ''))) {
            newErrors.phone = 'Please enter a valid Ghana phone number';
        }

        if (!formData.currentSystem) {
            newErrors.currentSystem = 'Current system information is required';
        }

        if (!formData.urgency) {
            newErrors.urgency = 'Implementation timeline is required';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setIsSubmitting(true);

        // Simulate API call
        try {
            await new Promise(resolve => setTimeout(resolve, 2000));
            setIsSubmitted(true);
            setSlotsRemaining(prev => prev - 1);
        } catch (error) {
            console.error('Form submission error:', error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isSubmitted) {
        return (
            <section id="demo-request" className="py-20 bg-gradient-to-br from-success/5 to-primary/5">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
                    <div className="bg-white rounded-2xl p-12 shadow-2xl">
                        <div className="w-20 h-20 bg-success/10 rounded-full flex items-center justify-center mx-auto mb-6">
                            <Icon name="CheckCircle" size={40} color="var(--color-success)" />
                        </div>

                        <h2 className="text-3xl font-bold text-foreground mb-4">
                            Demo Request Submitted Successfully!
                        </h2>

                        <p className="text-lg text-muted-foreground mb-8">
                            Thank you, {formData.fullName}. Our team will contact you within 24 hours to schedule
                            your personalized Civicrate demonstration for {formData.assembly}.
                        </p>

                        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                            <div className="text-center">
                                <Icon name="Calendar" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                                <p className="font-medium">Demo Scheduled</p>
                                <p className="text-sm text-muted-foreground">Within 24 hours</p>
                            </div>

                            <div className="text-center">
                                <Icon name="Phone" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                                <p className="font-medium">Contact Method</p>
                                <p className="text-sm text-muted-foreground">
                                    {formData.whatsappPreferred ? 'WhatsApp' : 'Phone'} + Email
                                </p>
                            </div>

                            <div className="text-center">
                                <Icon name="Clock" size={24} color="var(--color-primary)" className="mx-auto mb-2" />
                                <p className="font-medium">Demo Duration</p>
                                <p className="text-sm text-muted-foreground">45-60 minutes</p>
                            </div>
                        </div>

                        <div className="bg-primary/5 rounded-lg p-6">
                            <h3 className="font-semibold text-foreground mb-2">What to Expect in Your Demo</h3>
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm text-muted-foreground">
                                <div className="flex items-center space-x-2">
                                    <Icon name="Check" size={16} color="var(--color-success)" />
                                    <span>Live system walkthrough</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Icon name="Check" size={16} color="var(--color-success)" />
                                    <span>Custom implementation plan</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Icon name="Check" size={16} color="var(--color-success)" />
                                    <span>ROI calculations for your assembly</span>
                                </div>
                                <div className="flex items-center space-x-2">
                                    <Icon name="Check" size={16} color="var(--color-success)" />
                                    <span>Q&A with technical experts</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        );
    }

    const benefits: Benefit[] = [
        {
            icon: 'Monitor',
            title: 'Live System Walkthrough',
            description: 'See the complete Civicrate ecosystem in action with real data from similar assemblies'
        },
        {
            icon: 'Calculator',
            title: 'Custom ROI Analysis',
            description: 'Get personalized calculations showing potential revenue increases for your assembly'
        },
        {
            icon: 'Map',
            title: 'Implementation Roadmap',
            description: 'Detailed timeline and plan tailored to your assembly size and current systems'
        },
        {
            icon: 'Users',
            title: 'Training & Support Plan',
            description: 'Comprehensive onboarding strategy for your staff and citizens'
        }
    ];

    return (
        <section id="demo-request" className="py-20 bg-gradient-to-br from-primary/5 to-accent/5">
            <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="grid lg:grid-cols-2 gap-12 items-start">
                    {/* Form Section */}
                    <div className="bg-white rounded-2xl p-8 shadow-2xl">
                        {/* Urgency Banner */}
                        <div className="bg-warning/10 border border-warning/20 rounded-lg p-4 mb-8">
                            <div className="flex items-center space-x-3">
                                <Icon name="Clock" size={20} color="var(--color-warning)" />
                                <div>
                                    <p className="font-semibold text-warning">Limited Pilot Program</p>
                                    <p className="text-sm text-muted-foreground">
                                        Only {slotsRemaining} demo slots remaining this month
                                    </p>
                                </div>
                            </div>
                        </div>

                        <div className="mb-8">
                            <h2 className="text-3xl font-bold text-foreground mb-4">
                                Request Your Personalized Demo
                            </h2>
                            <p className="text-muted-foreground">
                                See how Civicrate can transform your assembly's revenue collection.
                                Get a customized demonstration based on your specific needs.
                            </p>
                        </div>

                        <form onSubmit={handleSubmit} className="space-y-6">
                            {/* Personal Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Full Name"
                                    type="text"
                                    placeholder="Enter your full name"
                                    value={formData.fullName}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('fullName', e.target.value)}
                                    error={errors.fullName}
                                    required
                                />

                                <Select
                                    label="Position"
                                    placeholder="Select your position"
                                    options={positionOptions}
                                    value={formData.position}
                                    onChange={(value) => handleSelectChange('position', value)}
                                    error={errors.position}
                                    required
                                />
                            </div>

                            {/* Assembly Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Select
                                    label="Assembly"
                                    placeholder="Select your assembly"
                                    options={assemblyOptions}
                                    value={formData.assembly}
                                    onChange={(value) => handleSelectChange('assembly', value)}
                                    error={errors.assembly}
                                    searchable
                                    required
                                />

                                <Select
                                    label="Assembly Size"
                                    placeholder="Select assembly type"
                                    options={assemblySizeOptions}
                                    value={formData.assemblySize}
                                    onChange={(value) => handleSelectChange('assemblySize', value)}
                                    error={errors.assemblySize}
                                    required
                                />
                            </div>

                            {/* Contact Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Input
                                    label="Email Address"
                                    type="email"
                                    placeholder="your.email@assembly.gov.gh"
                                    value={formData.email}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('email', e.target.value)}
                                    error={errors.email}
                                    required
                                />

                                <Input
                                    label="Phone Number"
                                    type="tel"
                                    placeholder="+233 XX XXX XXXX"
                                    value={formData.phone}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('phone', e.target.value)}
                                    error={errors.phone}
                                    required
                                />
                            </div>

                            {/* WhatsApp Preference */}
                            <div className="flex items-center space-x-3">
                                <input
                                    type="checkbox"
                                    id="whatsapp"
                                    checked={formData.whatsappPreferred}
                                    onChange={(e: ChangeEvent<HTMLInputElement>) => handleInputChange('whatsappPreferred', e.target.checked)}
                                    className="w-4 h-4 text-primary border-border rounded focus:ring-primary"
                                />
                                <label htmlFor="whatsapp" className="text-sm text-muted-foreground">
                                    Prefer WhatsApp for initial contact
                                </label>
                            </div>

                            {/* System Information */}
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <Select
                                    label="Current System"
                                    placeholder="Describe your current system"
                                    options={currentSystemOptions}
                                    value={formData.currentSystem}
                                    onChange={(value) => handleSelectChange('currentSystem', value)}
                                    error={errors.currentSystem}
                                    required
                                />

                                <Select
                                    label="Implementation Timeline"
                                    placeholder="When do you need this?"
                                    options={urgencyOptions}
                                    value={formData.urgency}
                                    onChange={(value) => handleSelectChange('urgency', value)}
                                    error={errors.urgency}
                                    required
                                />
                            </div>

                            {/* Challenges */}
                            <div>
                                <label className="block text-sm font-medium text-foreground mb-2">
                                    Current Challenges (Optional)
                                </label>
                                <textarea
                                    placeholder="Describe your main revenue collection challenges..."
                                    value={formData.challenges}
                                    onChange={(e: ChangeEvent<HTMLTextAreaElement>) => handleInputChange('challenges', e.target.value)}
                                    rows={3}
                                    className="w-full px-3 py-2 border border-border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
                                />
                            </div>

                            {/* Submit Button */}
                            <Button
                                type="submit"
                                variant="default"
                                size="lg"
                                loading={isSubmitting}
                                iconName="Calendar"
                                iconPosition="left"
                                fullWidth
                                className="text-lg py-4"
                            >
                                {isSubmitting ? 'Scheduling Demo...' : 'Schedule My Demo'}
                            </Button>

                            {/* Privacy Notice */}
                            <p className="text-xs text-muted-foreground text-center">
                                By submitting this form, you agree to our privacy policy.
                                We'll only use your information to schedule and conduct your demo.
                            </p>
                        </form>
                    </div>

                    {/* Benefits Section */}
                    <div className="space-y-8">
                        <div>
                            <h3 className="text-2xl font-bold text-foreground mb-6">
                                What You'll Get in Your Demo
                            </h3>

                            <div className="space-y-6">
                                {benefits.map((benefit, index) => (
                                    <div key={index} className="flex items-start space-x-4 p-6 bg-white rounded-xl shadow-md">
                                        <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                                            <Icon name={benefit.icon as keyof typeof LucideIcons} size={24} color="var(--color-primary)" />
                                        </div>
                                        <div>
                                            <h4 className="font-semibold text-foreground mb-2">{benefit.title}</h4>
                                            <p className="text-muted-foreground text-sm">{benefit.description}</p>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>

                        {/* Success Guarantee */}
                        <div className="bg-success/5 rounded-xl p-6 border border-success/20">
                            <div className="flex items-center space-x-3 mb-4">
                                <Icon name="ShieldCheck" size={24} color="var(--color-success)" />
                                <h4 className="font-semibold text-success">Implementation Guarantee</h4>
                            </div>
                            <p className="text-sm text-muted-foreground">
                                We guarantee successful implementation within 6 weeks or your money back.
                                Join 50+ assemblies already transforming their operations.
                            </p>
                        </div>

                        {/* Contact Alternative */}
                        <div className="bg-white rounded-xl p-6 shadow-md">
                            <h4 className="font-semibold text-foreground mb-4">Prefer to Talk First?</h4>
                            <div className="space-y-3">
                                <div className="flex items-center space-x-3">
                                    <Icon name="Phone" size={16} color="var(--color-primary)" />
                                    <span className="text-sm">+233 XX XXX XXXX</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Icon name="Mail" size={16} color="var(--color-primary)" />
                                    <span className="text-sm">demo@civicrate.gov.gh</span>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <Icon name="MessageCircle" size={16} color="var(--color-primary)" />
                                    <span className="text-sm">WhatsApp: +233 XX XXX XXXX</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
};

export default DemoRequestSection;